<template>
    <div class="min-h-[calc(100vh-64px)] bg-white flex flex-col items-center justify-center px-8 py-16">

        <!-- Heading -->
        <h1 class="text-5xl font-bold tracking-tight text-center mb-12 leading-tight">
            <span class="text-gray-900">Sur quel espace</span>
            <span class="text-gray-300"> travailler&nbsp;?</span>
        </h1>

        <!-- État vide -->
        <div v-if="availableSpaces.length === 0" class="flex flex-col items-center gap-3 py-16 text-gray-400">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
            </svg>
            <p class="text-sm text-center max-w-xs text-gray-400">
                Votre compte ne dispose d'aucun accès configuré.<br>Contactez votre administrateur.
            </p>
        </div>

        <!-- Grille des espaces -->
        <div
            v-else
            class="grid gap-4 w-full max-w-3xl"
            :class="{
                'grid-cols-1 max-w-sm': availableSpaces.length === 1,
                'grid-cols-2 max-w-xl': availableSpaces.length === 2,
                'grid-cols-3':          availableSpaces.length === 3,
            }"
        >
            <button
                v-for="space in availableSpaces"
                :key="space.id"
                class="group relative flex flex-col bg-white border border-gray-100 rounded-2xl overflow-hidden text-left transition-all duration-200 hover:shadow-xl hover:shadow-gray-100 hover:-translate-y-1 active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                :disabled="loadingSpace !== null"
                @click="openSpace(space)"
            >
                <!-- Zone illustration -->
                <div class="relative flex items-center justify-center h-52 overflow-hidden" :class="space.illustrationBg">
                    <!-- Icône de marque en haut à gauche -->
                    <div class="absolute top-3.5 left-3.5 w-7 h-7 rounded-lg flex items-center justify-center" :class="space.badgeBg">
                        <svg v-html="space.badgeIcon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="space.badgeColor"></svg>
                    </div>

                    <!-- Illustration SVG centrale -->
                    <div class="w-36 h-36 flex items-center justify-center" v-html="space.illustration"></div>

                    <!-- Loader overlay -->
                    <div v-if="loadingSpace === space.id" class="absolute inset-0 flex items-center justify-center backdrop-blur-[2px]" :class="space.illustrationBg">
                        <svg class="animate-spin w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" :class="space.badgeColor">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                        </svg>
                    </div>
                </div>

                <!-- Zone texte -->
                <div class="px-5 py-4 border-t border-gray-50">
                    <p class="text-[15px] font-bold text-gray-900 tracking-tight">{{ space.label }}</p>
                    <p class="text-xs text-gray-400 mt-0.5 leading-relaxed">{{ space.description }}</p>
                </div>
            </button>
        </div>

        <!-- Footer utilisateur -->
        <p class="mt-10 text-xs text-gray-300">
            {{ $account.account.email || $account.account.name }}
        </p>
    </div>
</template>

<script>
// ─── Configuration des URLs ───────────────────────────────────────────────────
const URLS = {
    bureau: {
        local: 'http://localhost:8144',
        prod:  'https://bureau.termoflow.fr',
    },
    admin: {
        local: 'http://localhost:8144',
        prod:  'https://admin.termoflow.fr',
    },
    commercial: {
        local: 'http://localhost:8146',
        prod:  'https://commercial.termoflow.fr',
    },
}

// ─── Définition des espaces ───────────────────────────────────────────────────
const SPACES = [
    {
        id:             'bureau',
        label:          'Termoflow',
        description:    'Gestion administrative, suivi des dossiers clients et pilotage de l\'activité.',
        illustrationBg: 'bg-blue-50',
        badgeBg:        'bg-blue-100',
        badgeColor:     'text-blue-500',
        badgeIcon:      `<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="12.01"/>`,
        illustration: `
            <svg viewBox="0 0 144 144" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- Bureau / plan de travail -->
                <ellipse cx="72" cy="112" rx="56" ry="10" fill="#BFDBFE" opacity="0.5"/>
                <!-- Corps du bureau -->
                <rect x="20" y="88" width="104" height="8" rx="4" fill="#93C5FD"/>
                <rect x="28" y="96" width="8" height="20" rx="2" fill="#60A5FA"/>
                <rect x="108" y="96" width="8" height="20" rx="2" fill="#60A5FA"/>
                <!-- Écran moniteur -->
                <rect x="40" y="44" width="64" height="44" rx="6" fill="white" stroke="#BFDBFE" stroke-width="2"/>
                <rect x="46" y="50" width="52" height="32" rx="3" fill="#EFF6FF"/>
                <!-- Contenu écran -->
                <rect x="50" y="55" width="30" height="3" rx="1.5" fill="#93C5FD"/>
                <rect x="50" y="62" width="44" height="2" rx="1" fill="#BFDBFE"/>
                <rect x="50" y="67" width="38" height="2" rx="1" fill="#BFDBFE"/>
                <rect x="50" y="72" width="42" height="2" rx="1" fill="#BFDBFE"/>
                <!-- Pied écran -->
                <rect x="65" y="88" width="14" height="4" rx="1" fill="#93C5FD"/>
                <rect x="60" y="91" width="24" height="3" rx="1.5" fill="#60A5FA"/>
                <!-- Tasse de café -->
                <rect x="94" y="76" width="14" height="12" rx="3" fill="white" stroke="#BFDBFE" stroke-width="1.5"/>
                <path d="M108 81 Q113 81 113 84 Q113 87 108 87" stroke="#BFDBFE" stroke-width="1.5" fill="none"/>
                <ellipse cx="101" cy="76" rx="7" ry="2" fill="#DBEAFE"/>
                <!-- Plante -->
                <rect x="24" y="74" width="10" height="14" rx="2" fill="#BFDBFE"/>
                <ellipse cx="29" cy="72" rx="8" ry="10" fill="#86EFAC"/>
                <ellipse cx="23" cy="68" rx="6" ry="8" fill="#4ADE80"/>
                <ellipse cx="35" cy="67" rx="6" ry="8" fill="#4ADE80"/>
                <ellipse cx="29" cy="62" rx="7" ry="9" fill="#22C55E"/>
            </svg>
        `,
        computedKey:   'showBureau',
        tokenEndpoint: '/mobile/generate-token-tech-mobile',
        urls:          URLS.bureau,
        next:          'Home',
    },
    {
        id:             'admin',
        label:          'Administrateur',
        description:    'Configuration de la plateforme, comptes utilisateurs et supervision du système.',
        illustrationBg: 'bg-rose-50',
        badgeBg:        'bg-rose-100',
        badgeColor:     'text-rose-500',
        badgeIcon:      `<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>`,
        illustration: `
            <svg viewBox="0 0 144 144" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- Ombre -->
                <ellipse cx="72" cy="118" rx="44" ry="8" fill="#FECDD3" opacity="0.5"/>
                <!-- Panneau de contrôle principal -->
                <rect x="22" y="32" width="100" height="76" rx="10" fill="white" stroke="#FECDD3" stroke-width="2"/>
                <rect x="22" y="32" width="100" height="22" rx="10" fill="#FFF1F2"/>
                <rect x="22" y="43" width="100" height="11" fill="#FFF1F2"/>
                <!-- Boutons header -->
                <circle cx="36" cy="43" r="5" fill="#FDA4AF"/>
                <circle cx="52" cy="43" r="5" fill="#FECDD3"/>
                <circle cx="68" cy="43" r="5" fill="#FEE2E2"/>
                <!-- Lignes de contenu -->
                <rect x="34" y="66" width="50" height="4" rx="2" fill="#FECDD3"/>
                <rect x="34" y="76" width="76" height="3" rx="1.5" fill="#FFE4E6"/>
                <rect x="34" y="83" width="60" height="3" rx="1.5" fill="#FFE4E6"/>
                <rect x="34" y="90" width="70" height="3" rx="1.5" fill="#FFE4E6"/>
                <!-- Bouton d'action -->
                <rect x="88" y="98" width="26" height="6" rx="3" fill="#FB7185"/>
                <!-- Bouclier central décoratif -->
                <path d="M96 34 Q96 22 108 18 Q120 22 120 34 Q120 44 108 50 Q96 44 96 34Z" fill="#FFF1F2" stroke="#FECDD3" stroke-width="1.5"/>
                <path d="M103 33 L106 36 L113 29" stroke="#FB7185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `,
        computedKey:   'showAdmin',
        tokenEndpoint: '/mobile/generate-token-tech-mobile',
        urls:          URLS.admin,
        next:          'AdminDashboard',
    },
    {
        id:             'commercial',
        label:          'Commercial',
        description:    'Prospects, devis, relances et tableau de bord des performances commerciales.',
        illustrationBg: 'bg-amber-50',
        badgeBg:        'bg-amber-100',
        badgeColor:     'text-amber-500',
        badgeIcon:      `<line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/>`,
        illustration: `
            <svg viewBox="0 0 144 144" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- Ombre -->
                <ellipse cx="72" cy="118" rx="44" ry="8" fill="#FDE68A" opacity="0.5"/>
                <!-- Fond carte dashboard -->
                <rect x="18" y="28" width="108" height="84" rx="10" fill="white" stroke="#FDE68A" stroke-width="2"/>
                <!-- Header carte -->
                <rect x="18" y="28" width="108" height="18" rx="10" fill="#FFFBEB"/>
                <rect x="18" y="36" width="108" height="10" fill="#FFFBEB"/>
                <rect x="28" y="34" width="40" height="4" rx="2" fill="#FDE68A"/>
                <!-- Barres du graphique -->
                <rect x="30" y="98" width="14" height="20" rx="3" fill="#FDE68A"/>
                <rect x="52" y="80" width="14" height="38" rx="3" fill="#FCD34D"/>
                <rect x="74" y="68" width="14" height="50" rx="3" fill="#FBBF24"/>
                <rect x="96" y="78" width="14" height="40" rx="3" fill="#F59E0B"/>
                <!-- Ligne de tendance -->
                <path d="M37 96 L59 78 L81 66 L103 76" stroke="#F59E0B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <!-- Points de la courbe -->
                <circle cx="37" cy="96" r="3" fill="white" stroke="#F59E0B" stroke-width="2"/>
                <circle cx="59" cy="78" r="3" fill="white" stroke="#F59E0B" stroke-width="2"/>
                <circle cx="81" cy="66" r="3" fill="white" stroke="#F59E0B" stroke-width="2"/>
                <circle cx="103" cy="76" r="3" fill="white" stroke="#F59E0B" stroke-width="2"/>
                <!-- Axes -->
                <line x1="26" y1="118" x2="120" y2="118" stroke="#FDE68A" stroke-width="1.5"/>
                <line x1="26" y1="55"  x2="26"  y2="118" stroke="#FDE68A" stroke-width="1.5"/>
                <!-- Pastille KPI -->
                <rect x="88" y="34" width="30" height="14" rx="7" fill="#FEF3C7"/>
                <text x="103" y="44" text-anchor="middle" font-size="8" font-weight="700" fill="#D97706">+18%</text>
            </svg>
        `,
        computedKey:   'showCommercial',
        tokenEndpoint: '/mobile/generate-token-tech-mobile',
        urls:          URLS.commercial,
        next:          'CommercialDashboard',
    },
]

export default {
    name: 'SpaceSelector',

    data() {
        return {
            loadingSpace: null,
        }
    },

    computed: {
        // ── Une computed par espace ────────────────────────────────────────

        showBureau() {
            return true
            // const a = this.$account?.account || {}
            // return a.bureau == 1 || a.admin == 1
        },

        showAdmin() {
            return this.$account.account.super_admin

            // const a = this.$account?.account || {}
            // return a.admin == 1
        },

        showCommercial() {
            return this.$account.account.commercial

            // const a = this.$account?.account || {}
            // return a.commercial == 1 || a.admin == 1
        },

        // ── Liste filtrée selon les permissions ───────────────────────────
        availableSpaces() {
            return SPACES.filter(space => this[space.computedKey])
        },

        isLocal() {
            return window.location.hostname === 'localhost'
        },
    },

    methods: {
        getBaseUrl(space) {
            return this.isLocal ? space.urls.local : space.urls.prod
        },

        async openSpace(space) {
            if (this.loadingSpace !== null) return
            this.loadingSpace = space.id

            try {
                const response = await this.$request.post(space.tokenEndpoint)

                if (!response.success) {
                    this.$toast.error(response.message || "Impossible de générer le token d'accès.")
                    return
                }

                const url = `${this.getBaseUrl(space)}/app/auth?token=${response.token}&session=1&redirect=${space.next}`
                window.location.href = url
            } catch (error) {
                this.$toast.error("Erreur lors de la connexion à l'espace.")
                console.error('[SpaceSelector]', error)
            } finally {
                this.loadingSpace = null
            }
        },
    },
}
</script>